import React, { useMemo } from 'react';

import { Col, Container, Row, Table } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { formatIsoToDate } from '../../utils';
import TableFilter from '../TableFilter';
import TablePagination from '../TablePagination';

import './styles.scoped.css';

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

const fileTypeBadgeStyle = {
  Partial: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: '#C8102E',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
  },
  Complete: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: '#53565A',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
  },
};

const DriverQualificationListTable = ({
  data = [],
  Route,
  showBranch = false,
  showFileType = false,
  showFilter = true,
  pageSize = 10,
  defaultSortField = 'id',
}) => {
  const columnClassName = 'align-middle text-nowrap';
  const columns = useMemo(
    () => [
      {
        Header: 'Number',
        accessor: 'id',
        className: columnClassName,
        Cell: instance => <Link to={`${Route}/${instance.value}`}>{instance.value}</Link>,
      },
      ...(showFileType
        ? [
            {
              Header: 'File',
              accessor: 'fileType',
              className: columnClassName,
              disableSortBy: true,
              Cell: instance => {
                if (instance.value === 'Partial') return <span style={fileTypeBadgeStyle.Partial}>P</span>;
                if (instance.value === 'Complete') return <span style={fileTypeBadgeStyle.Complete}>C</span>;
                return null;
              },
            },
          ]
        : []),
      ...(showBranch
        ? [
            {
              Header: 'Branch',
              accessor: 'branch',
              className: columnClassName,
            },
          ]
        : []),
      {
        Header: 'Driver',
        accessor: 'driverName',
        className: columnClassName,
      },
      {
        Header: 'City',
        accessor: 'locationCity',
        className: columnClassName,
        Cell: instance => {
          if (instance.value == null) return '';
          return typeof instance.value === 'string' ? instance.value : instance.value?.name || '';
        },
      },
      {
        Header: 'State',
        accessor: 'locationState',
        className: columnClassName,
        Cell: instance => {
          if (instance.value == null) return '';
          return typeof instance.value === 'string' ? instance.value : instance.value?.name || '';
        },
      },
      {
        Header: 'Status',
        accessor: 'status',
        className: columnClassName,
      },
      {
        Header: 'DQF Specialist',
        accessor: 'dqOwnerFullName',
        className: columnClassName,
      },
      {
        Header: 'Client Approver',
        accessor: 'dqClientApproverUserFullName',
        className: columnClassName,
      },
      {
        Header: 'Client Name',
        accessor: 'name',
        className: columnClassName,
      },
      {
        Header: 'Created',
        accessor: 'createdAt',
        className: columnClassName,
        Cell: instance => `${formatIsoToDate(instance.value)}`,
      },
      {
        Header: 'Updated',
        accessor: 'updatedAt',
        className: columnClassName,
        Cell: instance => `${formatIsoToDate(instance.value)}`,
      },
    ],
    [showBranch, showFileType, Route],
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize,
        sortBy: [
          {
            id: defaultSortField,
            desc: true,
          },
        ],
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, setFilter, setGlobalFilter } =
    tableInstance;

  function filter(event, columnName) {
    const filterColumn = columns.find(column => column.Header === columnName);
    if (filterColumn) {
      setFilter(filterColumn.accessor, event.target.value);
    } else {
      setGlobalFilter(event.target.value);
    }
  }

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => {
      const { key: thKey, ...thProps } = column.getHeaderProps(column.getSortByToggleProps());
      return (
        <th key={thKey} {...thProps}>
          {column.render('Header')}
          <span>{column.canSort ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}</span>
        </th>
      );
    });
    const { key: trKey, ...trProps } = headerGroup.getHeaderGroupProps();
    return <tr key={trKey} {...trProps}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => {
      const { key: tdKey, ...tdProps } = cell.getCellProps({
        className: cell.column.className,
        style: cell.column.style,
      });
      return (
        <td key={tdKey} {...tdProps}>
          {cell.render('Cell')}
        </td>
      );
    });

    const { key: trKey, ...trProps } = row.getRowProps();
    return <tr key={trKey} {...trProps}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="align-items-center">
        {showFilter && (
          <Col md={6} className="mr-auto">
            <TableFilter className="mb-3" columns={columns} onSearchChange={filter} />
          </Col>
        )}
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default DriverQualificationListTable;
